import React, { useCallback, useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { getAccountPnlHistory } from "../api";

const REFRESH_MS = 30000;
const MAX_DISPLAY_POINTS = 400; // downsample long ranges so the chart stays responsive

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

function formatTime(ts) {
  return new Date(ts).toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatNumber(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function downsample(points) {
  if (points.length <= MAX_DISPLAY_POINTS) return points;
  const stride = Math.ceil(points.length / MAX_DISPLAY_POINTS);
  return points.filter((_, i) => i % stride === 0 || i === points.length - 1);
}

export default function AccountPnlChart() {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rawPoints, setRawPoints] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback((start, end) => {
    setLoading(true);
    getAccountPnlHistory(start || undefined, end || undefined)
      .then((res) => {
        const { history, start: respStart, end: respEnd } = res.data;
        setRawPoints(history || []);
        setStartDate((prev) => prev || respStart);
        setEndDate((prev) => prev || respEnd);
        setError(null);
      })
      .catch((err) => setError(err.response?.data?.detail || err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load(null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!startDate || !endDate) return;
    const id = setInterval(() => load(startDate, endDate), REFRESH_MS);
    return () => clearInterval(id);
  }, [startDate, endDate, load]);

  const handleApply = () => {
    if (startDate && endDate) load(startDate, endDate);
  };

  const pnlValues = rawPoints.map((p) => p.pnl);
  const maxPnl = pnlValues.length ? Math.max(...pnlValues) : 0;
  const minPnl = pnlValues.length ? Math.min(...pnlValues) : 0;
  const latestPnl = pnlValues.length ? pnlValues[pnlValues.length - 1] : 0;

  const displayPoints = downsample(rawPoints).map((p) => ({
    time: formatTime(p.ts),
    pnl: p.pnl,
  }));

  const dataMax = Math.max(maxPnl, 0);
  const dataMin = Math.min(minPnl, 0);
  const zeroOffset = dataMax === dataMin ? 0.5 : dataMax / (dataMax - dataMin);

  const tickInterval = Math.max(0, Math.ceil(displayPoints.length / 8) - 1);

  return (
    <div className="account-pnl-chart">
      <div className="field-group inline">
        <div>
          <label>From</label>
          <input
            type="date"
            value={startDate || ""}
            max={endDate || isoDate(new Date())}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <label>To</label>
          <input
            type="date"
            value={endDate || ""}
            max={isoDate(new Date())}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div style={{ display: "flex", alignItems: "flex-end" }}>
          <button type="button" onClick={handleApply} disabled={loading}>
            {loading ? "Loading..." : "Apply"}
          </button>
        </div>
      </div>

      {error && <div className="banner error">{error}</div>}

      {!error && rawPoints.length < 2 && (
        <p className="muted">Not enough data yet for this range.</p>
      )}

      {!error && rawPoints.length >= 2 && (
        <>
          <div style={{ width: "100%", height: 260, marginTop: 8 }}>
            <ResponsiveContainer>
              <AreaChart data={displayPoints}>
                <defs>
                  <linearGradient id="acctSplitFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={zeroOffset} stopColor="#3ecf8e" stopOpacity={0.3} />
                    <stop offset={zeroOffset} stopColor="#f16063" stopOpacity={0.3} />
                  </linearGradient>
                  <linearGradient id="acctSplitLine" x1="0" y1="0" x2="0" y2="1">
                    <stop offset={zeroOffset} stopColor="#3ecf8e" />
                    <stop offset={zeroOffset} stopColor="#f16063" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2f3a" />
                <XAxis dataKey="time" stroke="#7b8291" fontSize={11} interval={tickInterval} />
                <YAxis stroke="#7b8291" fontSize={11} />
                <Tooltip
                  contentStyle={{ background: "#171a21", border: "1px solid #2a2f3a", fontSize: 12 }}
                  formatter={(value) => [formatNumber(value), "P&L"]}
                />
                <ReferenceLine y={0} stroke="#7b8291" />
                <ReferenceLine
                  y={maxPnl}
                  stroke="#3ecf8e"
                  strokeDasharray="3 3"
                  label={{ value: `Max ${formatNumber(maxPnl)}`, position: "insideTopLeft", fill: "#3ecf8e", fontSize: 11 }}
                />
                <ReferenceLine
                  y={minPnl}
                  stroke="#f16063"
                  strokeDasharray="3 3"
                  label={{ value: `Min ${formatNumber(minPnl)}`, position: "insideBottomLeft", fill: "#f16063", fontSize: 11 }}
                />
                <Area
                  type="monotone"
                  dataKey="pnl"
                  stroke="url(#acctSplitLine)"
                  fill="url(#acctSplitFill)"
                  strokeWidth={2}
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
            <div style={{ flex: 1, background: "#0f1115", border: "1px solid #2a2f3a", borderRadius: 8, padding: "10px 14px" }}>
              <div className="muted" style={{ fontSize: 12 }}>Max P&amp;L</div>
              <div className="pnl-positive" style={{ fontSize: 18 }}>{formatNumber(maxPnl)}</div>
            </div>
            <div style={{ flex: 1, background: "#0f1115", border: "1px solid #2a2f3a", borderRadius: 8, padding: "10px 14px" }}>
              <div className="muted" style={{ fontSize: 12 }}>Min P&amp;L</div>
              <div className="pnl-negative" style={{ fontSize: 18 }}>{formatNumber(minPnl)}</div>
            </div>
            <div style={{ flex: 1, background: "#0f1115", border: "1px solid #2a2f3a", borderRadius: 8, padding: "10px 14px" }}>
              <div className="muted" style={{ fontSize: 12 }}>Latest P&amp;L</div>
              <div className={latestPnl >= 0 ? "pnl-positive" : "pnl-negative"} style={{ fontSize: 18 }}>
                {formatNumber(latestPnl)}
              </div>
            </div>
          </div>

          {rawPoints.length > MAX_DISPLAY_POINTS && (
            <p className="hint" style={{ marginTop: 12 }}>
              Showing a downsampled view ({displayPoints.length} of {rawPoints.length} points) for
              readability. Max/Min/Latest above are computed from the full range, not just what's
              plotted.
            </p>
          )}
        </>
      )}
    </div>
  );
}
